import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Anchor, Tabs } from '@mantine/core';

import { useGetOrganisation } from 'api/organisations';
import { DYNAMIC_URLS } from 'consts';
import type { BoardType } from 'types';

import { GeneralSettings } from './GeneralSettings';
import { QueueGroups } from './QueueGroups';
import { Invitations } from './Invitations';
import { Members } from './Members';

type Props = {
  board: BoardType;
};

export const BoardViews = ({ board }: Props) => {
  const { t } = useTranslation('organisations');
  const { organisationId, settingsTabKey } = useParams() as {
    organisationId: string;
    settingsTabKey: string;
  };

  const [activeTab, setActiveTab] = useState<string | null>(settingsTabKey);

  const { data: organisation } = useGetOrganisation({
    pathParams: { organisationId },
  });
  const { isAdministrator } = organisation || {};

  const getBoardSettingsUrl = (tabKey: string) =>
    DYNAMIC_URLS.organisationBoardSettings({
      organisationId,
      boardId: board.id.toString(),
      settingsTabKey: tabKey,
    });

  const sx = { color: 'initial', '&:hover': { textDecoration: 'none' } };

  const hasQueueGroupsAccess = useMemo(
    () => board.__links.find(link => link.rel === 'queue_groups') !== undefined,
    [board],
  );

  useEffect(() => {
    setActiveTab(settingsTabKey);
  }, [settingsTabKey]);

  return (
    <Tabs variant="default" value={activeTab} onTabChange={setActiveTab}>
      <Tabs.List sx={theme => ({ marginBottom: theme.other.spacing(3) })}>
        <Anchor
          component={Link}
          to={getBoardSettingsUrl('general-settings')}
          sx={sx}
        >
          <Tabs.Tab value="general-settings">
            {t('organisationView.boards.tabs.general')}
          </Tabs.Tab>
        </Anchor>
        <Anchor component={Link} to={getBoardSettingsUrl('members')} sx={sx}>
          <Tabs.Tab value="members">
            {t('organisationView.boards.tabs.users')}
          </Tabs.Tab>
        </Anchor>
        {isAdministrator && hasQueueGroupsAccess && (
          <Anchor component={Link} to={getBoardSettingsUrl('groups')} sx={sx}>
            <Tabs.Tab value="groups">
              {t('organisationView.boards.tabs.groups')}
            </Tabs.Tab>
          </Anchor>
        )}
        {isAdministrator && (
          <Anchor
            component={Link}
            to={getBoardSettingsUrl('invitations')}
            sx={sx}
          >
            <Tabs.Tab value="invitations">
              {t('organisationView.boards.tabs.invitations')}
            </Tabs.Tab>
          </Anchor>
        )}
      </Tabs.List>
      <Tabs.Panel value="general-settings">
        <GeneralSettings board={board} />
      </Tabs.Panel>
      <Tabs.Panel value="members">
        <Members board={board} />
      </Tabs.Panel>
      {isAdministrator && hasQueueGroupsAccess && (
        <Tabs.Panel value="groups">
          <QueueGroups />
        </Tabs.Panel>
      )}
      <Tabs.Panel value="invitations">
        <Invitations />
      </Tabs.Panel>
    </Tabs>
  );
};
